import React from "react";
import { Shield, FileText, RefreshCw } from "lucide-react";

const Policy = () => {
  const PolicySection = ({ id, title, icon: Icon, children }) => (
    <div id={id} className="mb-20 scroll-mt-8">
      <div className="relative mb-8">
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 p-4 rounded-2xl shadow-lg">
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-yellow-500 via-yellow-600 to-green-600 bg-clip-text text-transparent">
            {title}
          </h2>
        </div>
        <div className="h-1 w-32 mx-auto bg-gradient-to-r from-yellow-400 to-green-500 rounded-full"></div>
      </div>
      
      <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
        {children}
      </div>
    </div>
  );

  const ListItem = ({ title, items }) => (
    <li className="mb-6 group">
      <div className="font-semibold text-lg text-gray-800 mb-3 flex items-start">
        <span className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center text-white text-sm mr-3 flex-shrink-0 group-hover:scale-110 transition-transform">
          {title.match(/^\d+/)?.[0] || "•"}
        </span>
        <span className="pt-1">{title}</span>
      </div>
      <ul className="space-y-3 ml-11">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start text-gray-700 leading-relaxed">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            <span dangerouslySetInnerHTML={{ __html: item }}></span>
          </li>
        ))}
      </ul>
    </li>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-green-50 py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-yellow-500 via-yellow-600 to-green-600 bg-clip-text text-transparent">
            Legal Information
          </h1>
          <p className="text-gray-600 text-lg">Everything you need to know about using Funingo</p>
        </div>

        {/* Terms and Conditions */}
        <PolicySection id="terms" title="Terms and Conditions" icon={FileText}>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Welcome to Funingo! <br /><br />
              These terms and conditions outline the rules and regulations for the use of Funingo's Website, located at funingo.fun.
              <br /><br />
              By accessing this website, we assume you accept these terms and conditions. Do not continue to use Funingo if you do not agree to all of the terms and conditions stated on this page.
            </p>
            
            <ol className="space-y-4 list-none">
              <ListItem 
                title="1. Use of Website:"
                items={[
                  "By accessing this website, you agree to use it only for lawful purposes and in a way that does not infringe the rights of, restrict, or inhibit anyone else's use and enjoyment of the website.",
                  "You are not allowed to use this website for any illegal activities, or to promote or transmit material that may violate applicable laws or regulations."
                ]}
              />
              
              <ListItem 
                title="2. Ticket Purchase and Admission:"
                items={[
                  "All tickets purchased online or at the gate are subject to availability.",
                  "Tickets are non-transferable and can only be used by the person for whom they are purchased.",
                  "Funingo reserves the right to deny admission to anyone who does not comply with park rules and regulations."
                ]}
              />
              
              <ListItem 
                title="3. Limitation of Liability:"
                items={[
                  "Funingo is not responsible for any direct, indirect, incidental, or consequential damages arising out of your use of this website or your visit to the park."
                ]}
              />
              
              <ListItem 
                title="4. Governing Law:"
                items={[
                  "These terms and conditions are governed by and construed in accordance with the laws of India, and you submit to the exclusive jurisdiction of the courts in India."
                ]}
              />
              
              <ListItem 
                title="5. Changes to Terms:"
                items={[
                  "Funingo reserves the right to change these terms and conditions at any time. Your continued use of the website after any such changes will constitute your acceptance of the new terms."
                ]}
              />
            </ol>
          </div>
        </PolicySection>

        {/* Privacy Policy */}
        <PolicySection id="privacy-policy" title="Privacy Policy" icon={Shield}>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Your privacy is important to us. <br /><br />
              This Privacy Policy document contains types of information that is collected and recorded by Funingo and how we use it.
            </p>
            
            <ol className="space-y-4 list-none">
              <ListItem 
                title="1. Information We Collect:"
                items={[
                  "We collect information that you voluntarily provide to us when you make a purchase, register for an account, or contact us via the website.",
                  "This information may include your name, email address, phone number, payment details, and any other details required to process your order or respond to your inquiries."
                ]}
              />
              
              <ListItem 
                title="2. How We Use Your Information:"
                items={[
                  "To process transactions and deliver the products and services you have requested.",
                  "To improve our website and services by analyzing user behavior and preferences.",
                  "To send periodic emails regarding your order or other products and services."
                ]}
              />
              
              <ListItem 
                title="3. Data Security:"
                items={[
                  "We use appropriate security measures to protect your personal information from unauthorized access, alteration, or destruction.",
                  "However, we cannot guarantee the absolute security of your information, and you share information with us at your own risk."
                ]}
              />
              
              <ListItem 
                title="4. Third-Party Services:"
                items={[
                  "We may use third-party services, such as payment processors, to facilitate transactions. These third parties are only permitted to use your information as necessary to provide these services to us."
                ]}
              />
              
              <ListItem 
                title="5. Your Rights:"
                items={[
                  "You have the right to access, correct, or delete your personal information stored by us. If you wish to exercise any of these rights, please contact us."
                ]}
              />
              
              <ListItem 
                title="6. Changes to This Policy:"
                items={[
                  "We may update our Privacy Policy from time to time. Any changes will be posted on this page, and the revised date will be updated accordingly."
                ]}
              />
            </ol>
          </div>
        </PolicySection>

        {/* Refund Policy */}
        <PolicySection id="refund-policy" title="Refund Policy" icon={RefreshCw}>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Thank You for Choosing Funingo Adventure Arena! <br /><br />
              We want you to have a great time with us. However, if plans change, please check our refund policy:
            </p>
            
            <ol className="space-y-4 list-none">
              <ListItem 
                title="1. Ticket Refunds:"
                items={[
                  "<strong>No Refunds:</strong> All ticket sales are final. We do not offer refunds.",
                  "<strong>Park Closures:</strong> If the park closes, you can reschedule your tickets for another date, based on availability."
                ]}
              />
              
              <ListItem 
                title="2. Special Events:"
                items={[
                  "<strong>Non-Refundable:</strong> Tickets for special events (like concerts, shows, and seasonal activities) cannot be refunded."
                ]}
              />
              
              <ListItem 
                title="3. Merchandise and Food Purchases:"
                items={[
                  "<strong>No Refunds:</strong> We don't offer refunds on merchandise unless there's a defect or mistake.",
                  "In case of defect one needs to raise a refund request within 15 days along with the receipt, if request is accepted, refund will be processed with 15 working days from the time of request approval."
                ]}
              />
              
              <ListItem 
                title="4. Cancellation Policy:"
                items={[
                  "<strong>Rescheduling:</strong> If you need to cancel or change your visit, contact us at least 48 hours before your scheduled date. We'll try to help, but changes depend on availability and our policies."
                ]}
              />
              
              <ListItem 
                title="5. Contact Us:"
                items={[
                  "If you have any questions about our refund policy, please contact us at (+91 787-9333-731) or (+91 942-5850-422)."
                ]}
              />
            </ol>
          </div>
        </PolicySection>

        {/* Footer Note */}
        <div className="text-center mt-16 p-8 bg-gradient-to-r from-yellow-100 to-green-100 rounded-2xl">
          <p className="text-gray-700 text-lg">
            Have questions? We're here to help! Contact us anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Policy;